import { useState } from "react";
import { Check, Plus, FileSignature } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useLoad, Loading, StatusBadge, fmtDate, gbp, STAGE_LABELS, STAGE_ORDER } from "./shared";
import { ProposalDialog } from "./CommerceProposals";

const Stepper = ({ stage }) => {
  const idx = STAGE_ORDER.indexOf(stage);
  return (
    <ol className="grid grid-cols-5 gap-y-3 gap-x-1" data-testid="client-stage-stepper">
      {STAGE_ORDER.map((s, i) => (
        <li key={s} className="flex flex-col items-center text-center">
          <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold ${i < idx ? "bg-emerald-500 text-white" : i === idx ? "bg-indigo-600 text-white ring-4 ring-indigo-100" : "bg-slate-100 text-slate-400"}`}>
            {i < idx ? <Check className="h-3 w-3" /> : i + 1}
          </span>
          <span className={`mt-1 text-[10px] leading-tight ${i === idx ? "text-indigo-700 font-semibold" : "text-slate-500"}`}>{STAGE_LABELS[s]}</span>
        </li>
      ))}
    </ol>
  );
};

const Records = ({ title, items, render, testid }) => (
  <div data-testid={testid}>
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{title} ({items.length})</p>
    {items.length ? <ul className="space-y-1.5">{items.map(render)}</ul> : <p className="text-xs text-slate-400">None</p>}
  </div>
);

const Row = ({ k, left, right }) => (
  <li key={k} className="flex items-center justify-between gap-3 text-sm bg-slate-50 rounded-lg px-3 py-2">
    <span className="min-w-0 truncate">{left}</span><span className="shrink-0">{right}</span>
  </li>
);

const Projects = ({ call, clientId, projects, reload }) => {
  const [title, setTitle] = useState("");
  const add = async () => {
    if (!title.trim()) return;
    await call("/projects", { method: "POST", body: { client_id: clientId, title } });
    setTitle(""); toast.success("Project created"); reload();
  };
  const setStatus = async (id, status) => { await call(`/projects/${id}`, { method: "PATCH", body: { status } }); reload(); };
  return (
    <div data-testid="client-projects">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Projects ({projects.length})</p>
      <ul className="space-y-1.5 mb-2">
        {projects.map(p => (
          <li key={p.project_id} className="flex items-center justify-between gap-3 text-sm bg-slate-50 rounded-lg px-3 py-2">
            <span className="truncate">{p.title}</span>
            <Select value={p.status} onValueChange={v => setStatus(p.project_id, v)}>
              <SelectTrigger className="h-7 w-32" data-testid={`project-status-${p.project_id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{["planned", "in_progress", "on_hold", "completed"].map(s => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <Input placeholder="New project title" value={title} onChange={e => setTitle(e.target.value)} className="h-8" data-testid="new-project-title" />
        <Button size="sm" variant="outline" onClick={add} data-testid="new-project-btn"><Plus className="h-3.5 w-3.5" /></Button>
      </div>
    </div>
  );
};

export const ClientDetailSheet = ({ call, clientId, onClose }) => {
  const { data, loading, reload } = useLoad(call, `/clients/${clientId}`);
  const [proposing, setProposing] = useState(false);
  const c = data?.client;
  const update = async (body, msg) => { await call(`/clients/${clientId}`, { method: "PATCH", body }); toast.success(msg); reload(); };

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="client-detail-sheet">
        {loading || !c ? <Loading /> : (
          <div className="space-y-6">
            <SheetHeader>
              <SheetTitle data-testid="client-detail-name">{c.name}</SheetTitle>
              <p className="text-sm text-slate-500">{c.email}{c.phone ? ` · ${c.phone}` : ""}{c.company ? ` · ${c.company}` : ""}</p>
              <p className="text-xs text-slate-400">Sources: {c.sources?.join(", ")} · Marketing consent: {c.marketing_consent ? "Yes" : "No"} · Since {fmtDate(c.created_at)}</p>
            </SheetHeader>
            <Stepper stage={c.stage} />
            <div className="flex flex-wrap items-center gap-2">
              <Select value={c.stage} onValueChange={v => update({ stage: v }, "Stage updated")}>
                <SelectTrigger className="w-48" data-testid="client-stage-override"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGE_ORDER.map(s => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={() => setProposing(true)} data-testid="client-new-proposal-btn"><FileSignature className="h-3.5 w-3.5 mr-1" />New proposal</Button>
              {c.monthly_retainer?.status === "active" && (
                <Button size="sm" variant="outline" className="text-rose-600" onClick={() => update({ retainer_status: "cancelled" }, "Retainer cancelled")} data-testid="client-cancel-retainer-btn">Cancel retainer</Button>
              )}
            </div>
            {c.monthly_retainer && (
              <p className="text-sm" data-testid="client-retainer">Monthly retainer: <b>{gbp(c.monthly_retainer.amount)}/mo</b> · <StatusBadge status={c.monthly_retainer.status === "active" ? "confirmed" : "cancelled"} /></p>
            )}
            <Records testid="client-enquiries" title="Enquiries" items={data.enquiries} render={e => <Row k={e.enquiry_id} left={(e.services_needed || []).join(", ") || "General enquiry"} right={fmtDate(e.created_at)} />} />
            <Records testid="client-bookings" title="Consultations" items={data.bookings} render={b => <Row k={b.booking_ref} left={`${b.booking_ref} · ${b.confirmed_date || b.preferred_date}`} right={<StatusBadge status={b.status} />} />} />
            <Records testid="client-proposals" title="Proposals" items={data.proposals} render={p => <Row k={p.proposal_id} left={`${p.title} · ${gbp(p.price_amount)}${p.billing_type === "monthly" ? "/mo" : ""}`} right={<StatusBadge status={p.status} />} />} />
            <Records testid="client-agreements" title="Agreements" items={data.agreements} render={a => <Row k={a.agreement_id} left={a.title} right={<StatusBadge status={a.status} />} />} />
            <Records testid="client-invoices" title="Invoices" items={data.invoices} render={i => <Row k={i.invoice_id} left={`${i.invoice_number} · ${gbp(i.total)}`} right={<StatusBadge status={i.status} />} />} />
            <Records testid="client-payments" title="Payments received" items={data.payments} render={p => <Row k={p.payment_id} left={`${fmtDate(p.paid_at)} · ${p.method.replace("_", " ")}`} right={<b className="text-emerald-700">{gbp(p.amount)}</b>} />} />
            <Projects call={call} clientId={clientId} projects={data.projects} reload={reload} />
          </div>
        )}
        {proposing && <ProposalDialog call={call} clientId={clientId} onClose={() => setProposing(false)} onSaved={() => { setProposing(false); reload(); }} />}
      </SheetContent>
    </Sheet>
  );
};
